import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { StockPlan } from '../models/StockPickTypes';
import type { PlanExecutionData, PlanSell } from '../models/PlanExecutionTypes';
import { getPlanPurchases } from '../models/PlanExecutionTypes';
import { formatAmountForPdf, formatDateForPdf } from './pdfFormat';
import { grossAmount, sumCharges } from './planExecutionCalculations';
import {
  capClassLabel,
  holdingTypeLabel,
  planDisplayTitle,
  planStatusLabel,
  stockProfileDisplayName,
} from '../models/StockPickTypes';

export type StockPlanReportView = 'ADMIN' | 'PARTNER';

export interface StockPlanPdfOptions {
  view?: StockPlanReportView;
  execution?: PlanExecutionData;
  partnerId?: string;
  filename?: string;
}

function wrapText(doc: jsPDF, text: string, maxWidth: number): string[] {
  return doc.splitTextToSize(text || '-', maxWidth) as string[];
}

function addSection(doc: jsPDF, title: string, body: string, y: number, margin: number, pageWidth: number): number {
  const contentWidth = pageWidth - margin * 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(title, margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const lines = wrapText(doc, body, contentWidth);
  lines.forEach((line) => {
    if (y > 280) {
      doc.addPage();
      y = 20;
    }
    doc.text(line, margin, y);
    y += 5;
  });
  return y + 4;
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y > 280 - needed) {
    doc.addPage();
    return 20;
  }
  return y;
}

function filterAllocations(plan: StockPlan, partnerId?: string) {
  const allocations = plan.capitalAllocations?.filter((a) => a.capitalPartnerId) ?? [];
  if (!partnerId) return allocations;
  return allocations.filter((a) => a.capitalPartnerId === partnerId);
}

function appendExecutionPdf(
  doc: jsPDF,
  execution: PlanExecutionData,
  y: number,
  margin: number,
  partnerView: boolean
): number {
  const purchases = getPlanPurchases(execution);
  const sells = execution.sells ?? [];
  if (purchases.length === 0 && sells.length === 0) return y;

  y = ensureSpace(doc, y, 40);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Execution', margin, y);
  y += 8;

  purchases.forEach((purchase, idx) => {
    const gross = grossAmount(purchase.quantity, purchase.pricePerShare);
    const charges = sumCharges(purchase.charges);
    const lotCost = gross + charges;
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [[`Purchase ${idx + 1}`, 'Value']],
      body: [
        ['Trade date', formatDateForPdf(purchase.tradeDate)],
        ['Quantity', String(purchase.quantity)],
        ['Price / share', formatAmountForPdf(purchase.pricePerShare)],
        ['Gross value', formatAmountForPdf(gross)],
        ['Charges', formatAmountForPdf(charges)],
        ['Lot cost', formatAmountForPdf(lotCost)],
        ...(purchase.comments?.trim() ? [['Comments', purchase.comments]] : []),
      ],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [21, 101, 192] },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  });
  if (purchases.length > 1 && execution.totalPurchaseCost != null) {
    doc.setFontSize(9);
    doc.text(`Total purchase cost: ${formatAmountForPdf(execution.totalPurchaseCost)}`, margin, y);
    y += 6;
  }

  if (sells.length > 0) {
    const head = partnerView
      ? [['Date', 'Qty', 'Price', 'CG type', 'Comments']]
      : [['Date', 'Qty', 'Price', 'CG type', 'Gross', 'Comments']];
    const body = sells.map((sell: PlanSell) => {
      const gross = grossAmount(sell.quantity, sell.pricePerShare);
      const base = [
        formatDateForPdf(sell.tradeDate),
        String(sell.quantity),
        formatAmountForPdf(sell.pricePerShare),
        sell.capitalGainsType ?? 'STCG',
      ];
      if (!partnerView) base.push(formatAmountForPdf(gross));
      base.push(sell.comments?.trim() || '-');
      return base;
    });
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head,
      body,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [230, 81, 0] },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
    if (partnerView) {
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text('Profit and tax figures are omitted in partner view.', margin, y);
      y += 6;
      doc.setTextColor(0);
    }
  }
  return y;
}

export function exportStockPlanPdf(
  plan: StockPlan,
  optionsOrFilename?: StockPlanPdfOptions | string
): void {
  const options: StockPlanPdfOptions =
    typeof optionsOrFilename === 'string' ? { filename: optionsOrFilename } : optionsOrFilename ?? {};
  const partnerView = options.view === 'PARTNER';
  const execution = options.execution;
  const profile = plan.profile;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(partnerView ? 'Stock Plan — Partner View' : 'Stock Plan — Investment Review', margin, y);
  y += 10;

  doc.setFontSize(12);
  doc.text(planDisplayTitle(plan), margin, y);
  y += 6;
  if (profile) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(stockProfileDisplayName(profile), margin, y);
    y += 6;
  }

  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`Generated: ${formatDateForPdf(new Date().toISOString())}`, margin, y);
  y += 10;
  doc.setTextColor(0);

  const profileRows: string[][] = profile
    ? [
        ['Stock', stockProfileDisplayName(profile)],
        ['NSE Code', profile.nseCode || '-'],
        ['BSE Code', profile.bseCode || '-'],
        ['Domain', profile.domain || '-'],
        ['Cap', capClassLabel(profile.cap)],
      ]
    : [];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Field', 'Value']],
    body: [
      ...profileRows,
      ['Plan ID', plan.planCode || '-'],
      ['Plan label', plan.planLabel || '-'],
      ['Stage', planStatusLabel(plan.planStatus)],
      ['Holding horizon', holdingTypeLabel(plan.holdingType)],
      ['Planned capital', plan.plannedCapital != null ? formatAmountForPdf(plan.plannedCapital) : '-'],
      ['Created', formatDateForPdf(plan.createdDate)],
      ['Last updated', formatDateForPdf(plan.updatedDate)],
      ...(plan.totalAllocatedCapital != null
        ? [['Total allocated', formatAmountForPdf(plan.totalAllocatedCapital)]]
        : []),
    ],
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [21, 101, 192] },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  y = addSection(doc, 'Plan & reasons', plan.planAndReasons, y, margin, pageWidth);
  y = addSection(doc, 'Exit strategy', plan.exitStrategy || '-', y, margin, pageWidth);
  y = addSection(doc, 'Risk mitigation', plan.riskMitigation || '-', y, margin, pageWidth);

  const links = plan.externalLinks?.filter((l) => l.url?.trim()) ?? [];
  if (links.length > 0) {
    y = ensureSpace(doc, y, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Reference links', margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    links.forEach((link, idx) => {
      y = ensureSpace(doc, y, 8);
      const label = link.label?.trim() || `Link ${idx + 1}`;
      doc.text(`${label}: ${link.url}`, margin, y);
      y += 5;
    });
    y += 4;
  }

  const allocations = filterAllocations(plan, options.partnerId);
  if (allocations.length > 0 || (plan.totalAllocatedCapital != null && plan.totalAllocatedCapital > 0 && !options.partnerId)) {
    y = ensureSpace(doc, y, 40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(partnerView ? 'Capital on this plan' : 'Capital partner positions', margin, y);
    y += 6;
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Partner', 'Allocated amount', 'Note']],
      body: allocations.map((a) => [
        a.partnerName || `Partner #${a.capitalPartnerId}`,
        formatAmountForPdf(a.allocatedAmount),
        a.note || '-',
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [46, 125, 50] },
      foot:
        !options.partnerId && plan.totalAllocatedCapital != null
          ? [['Total', formatAmountForPdf(plan.totalAllocatedCapital), '']]
          : options.partnerId
            ? [
                [
                  'Your total',
                  formatAmountForPdf(allocations.reduce((s, a) => s + a.allocatedAmount, 0)),
                  '',
                ],
              ]
            : undefined,
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  if (execution) {
    y = appendExecutionPdf(doc, execution, y, margin, partnerView);
  }

  const result = plan.result;
  if (!partnerView && result && (result.investedAmount != null || result.quantity != null || result.taxAmount != null || result.profitAmount != null || result.calculatedProfitAmount != null || result.learning?.trim())) {
    y = ensureSpace(doc, y, 50);
    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Field', 'Value']],
      body: [
        ['Capital used', result.investedAmount != null ? formatAmountForPdf(result.investedAmount) : '-'],
        ['Shares bought', result.quantity != null ? String(result.quantity) : '-'],
        ['Tax paid', result.taxAmount != null ? formatAmountForPdf(result.taxAmount) : '-'],
        ['Profit (from sells)', result.calculatedProfitAmount != null ? formatAmountForPdf(result.calculatedProfitAmount) : '-'],
        ...(result.profitOverride != null
          ? [['Admin profit override', formatAmountForPdf(result.profitOverride)]]
          : []),
        ['Effective profit', result.profitAmount != null ? formatAmountForPdf(result.profitAmount) : '-'],
        ['Execution date', result.resultDate ? formatDateForPdf(result.resultDate) : '-'],
      ],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [230, 81, 0] },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
    y = addSection(doc, 'Result notes', result.learning || '-', y, margin, pageWidth);
  }

  const comments = plan.comments ?? [];
  if (comments.length > 0) {
    y = ensureSpace(doc, y, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Timeline notes (comments)', margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    comments.forEach((comment) => {
      y = ensureSpace(doc, y, 20);
      doc.setFont('helvetica', 'bold');
      doc.text(formatDateForPdf(comment.createdDate), margin, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const lines = wrapText(doc, comment.body, pageWidth - margin * 2);
      lines.forEach((line) => {
        y = ensureSpace(doc, y, 6);
        doc.text(line, margin, y);
        y += 4.5;
      });
      y += 3;
    });
  }

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('Prepared via Whatzneu Capitals — for review only, not investment advice.', margin, 290);

  const safeName = (plan.planCode ?? 'plan').replace(/[^\w-]+/g, '_').slice(0, 40);
  const suffix = partnerView ? '-partner' : '';
  doc.save(options.filename ?? `stock-plan-${safeName}${suffix}.pdf`);
}
